import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PaginationInstance } from 'ngx-pagination';
import { NgxSpinnerService } from 'ngx-spinner';
import { TransactionService } from 'app/services/transaction.service';
import { Excel } from 'app/utils/excel';

interface Columna {
  campo: string;
  titulo: string;
  formato?: 'moneda' | 'numero' | 'codigo' | 'fecha' | 'situacion' | 'alerta';
  ancho?: number;
}

@Component({
  selector: 'app-movement-reports',
  templateUrl: './movement-reports.component.html',
  styleUrls: ['./movement-reports.component.css']
})
export class MovementReportsComponent implements OnInit {

  // La tabla y el Excel se generan de este mismo mapa para que no
  // puedan desalinearse. Los campos son las claves que devuelve el API.
  readonly COLUMNAS: { [tipo: string]: Columna[] } = {
    'gate-in': [
      { campo: 'fechaEntrada', titulo: 'Fecha entrada', formato: 'fecha', ancho: 17 },
      { campo: 'contenedor', titulo: 'Contenedor', formato: 'codigo', ancho: 15 },
      { campo: 'tipo', titulo: 'Tipo', ancho: 8 },
      { campo: 'operacion', titulo: 'Operación', ancho: 12 },
      { campo: 'conductor', titulo: 'Conductor', ancho: 22 },
      { campo: 'cedula', titulo: 'Cédula', ancho: 13 },
      { campo: 'placa', titulo: 'Placa', ancho: 9 },
      { campo: 'descripcion', titulo: 'Descripción', ancho: 18 },
      { campo: 'estado', titulo: 'Estado', ancho: 12 },
      { campo: 'alerta', titulo: 'Alerta', formato: 'alerta', ancho: 15 },
    ],
    'gate-out': [
      { campo: 'fechaSalida', titulo: 'Fecha salida', formato: 'fecha', ancho: 17 },
      { campo: 'fechaEntrada', titulo: 'Fecha entrada', formato: 'fecha', ancho: 17 },
      { campo: 'contenedor', titulo: 'Contenedor', formato: 'codigo', ancho: 15 },
      { campo: 'tipo', titulo: 'Tipo', ancho: 8 },
      { campo: 'operacion', titulo: 'Operación', ancho: 12 },
      { campo: 'diasEnPatio', titulo: 'Días en patio', formato: 'numero', ancho: 13 },
      { campo: 'conductorEntrada', titulo: 'Conductor entrada', ancho: 22 },
      { campo: 'placaEntrada', titulo: 'Placa entrada', ancho: 13 },
      { campo: 'transporte', titulo: 'Transporte', ancho: 14 },
      { campo: 'descuento', titulo: 'Descuento', formato: 'moneda', ancho: 12 },
      { campo: 'monto', titulo: 'Monto', formato: 'moneda', ancho: 13 },
      { campo: 'alerta', titulo: 'Alerta', formato: 'alerta', ancho: 15 },
    ],
    'transport': [
      { campo: 'fechaSalida', titulo: 'Fecha salida', formato: 'fecha', ancho: 17 },
      { campo: 'contenedor', titulo: 'Contenedor', formato: 'codigo', ancho: 15 },
      { campo: 'tipo', titulo: 'Tipo', ancho: 8 },
      { campo: 'transporte', titulo: 'Transporte', ancho: 14 },
      { campo: 'conductorEntrada', titulo: 'Conductor entrada', ancho: 22 },
      { campo: 'placaEntrada', titulo: 'Placa entrada', ancho: 13 },
      { campo: 'alerta', titulo: 'Alerta', formato: 'alerta', ancho: 15 },
    ],
    'storage-days': [
      { campo: 'contenedor', titulo: 'Contenedor', formato: 'codigo', ancho: 15 },
      { campo: 'tipo', titulo: 'Tipo', ancho: 8 },
      { campo: 'fechaEntrada', titulo: 'Entrada', formato: 'fecha', ancho: 17 },
      { campo: 'fechaSalida', titulo: 'Salida', formato: 'fecha', ancho: 17 },
      { campo: 'dias', titulo: 'Días', formato: 'numero', ancho: 7 },
      { campo: 'diasLibres', titulo: 'Libres', formato: 'numero', ancho: 8 },
      { campo: 'diasCobrables', titulo: 'Cobrables', formato: 'numero', ancho: 10 },
      { campo: 'tarifaDia', titulo: 'Tarifa día', formato: 'moneda', ancho: 12 },
      { campo: 'monto', titulo: 'Monto', formato: 'moneda', ancho: 13 },
      { campo: 'situacion', titulo: 'Situación', formato: 'situacion', ancho: 22 },
      { campo: 'alerta', titulo: 'Alerta', formato: 'alerta', ancho: 15 },
    ],
  };

  // Rampa ordinal de antigüedad. Debe coincidir con las variables
  // --edad-* del CSS: mismo tono, claro→oscuro, validada como conjunto.
  readonly COLORES_EDAD: { [id: string]: string } = {
    '0-7': '#86b6ef',
    '8-15': '#5598e7',
    '16-30': '#2a78d6',
    '31-60': '#1c5cab',
    '60+': '#0d366b',
  };

  readonly CLASES_EDAD: { [id: string]: string } = {
    '16-30': 'mr-edad-16-30',
    '31-60': 'mr-edad-31-60',
    '60+': 'mr-edad-60mas',
  };

  readonly TIPOS = [
    { id: 'gate-in', nombre: 'Gate In (entradas al patio)', archivo: 'GateIn' },
    { id: 'gate-out', nombre: 'Gate Out (salidas y despachos)', archivo: 'GateOut' },
    { id: 'storage-days', nombre: 'Almacenaje (días en patio)', archivo: 'Almacenaje' },
    { id: 'transport', nombre: 'Transporte al retirar', archivo: 'Transporte' },
  ];

  /**
   * Paleta CATEGÓRICA (identidad), distinta de la rampa ordinal de
   * antigüedad: aquí las categorías no tienen orden de magnitud.
   * Son los tres primeros tonos de la paleta de la guía, validados como
   * conjunto. "Sin dato" va en gris neutro a propósito: es ausencia de
   * dato, no una categoría más, y no debe llevar color de serie.
   */
  readonly COLORES_TRANSPORTE: { [k: string]: string } = {
    'Piki 7': '#2a78d6',
    'Externo': '#eb6834',
    'No requirió': '#1baf7a',
    'Sin dato': '#b0ada4',
  };

  filtros: FormGroup;
  tipoReporte = 'gate-in';
  loading = false;
  error = '';
  rows: any[] = [];
  totals: any = null;
  periodo: any = null;
  containerTypes: any[] = [];

  config: PaginationInstance = { itemsPerPage: 25, currentPage: 1 };

  constructor(
    private transactionService: TransactionService,
    private fb: FormBuilder,
    private spinner: NgxSpinnerService,
    private excel: Excel
  ) {
    this.filtros = this.fb.group({
      startDate: [''],
      endDate: [''],
      containerTypeId: [''],
    });
  }

  ngOnInit(): void {
    // Alimenta el filtro opcional por tipo de contenedor. Si falla, el
    // reporte sigue sirviendo sin ese filtro: no es un error bloqueante.
    this.transactionService.getContainerTypes().subscribe({
      next: (res: any) => { this.containerTypes = res || []; },
      error: () => { this.containerTypes = []; }
    });
  }

  get columnas(): Columna[] {
    return this.COLUMNAS[this.tipoReporte];
  }

  /** Rangos de antigüedad con su color y su participación en la plata detenida. */
  get antiguedad(): any[] {
    const rangos = (this.totals && this.totals.antiguedad) || [];
    const total = rangos.reduce((s: number, r: any) => s + r.monto, 0);
    return rangos.map((r: any) => ({
      ...r,
      color: this.COLORES_EDAD[r.id],
      pct: total > 0 ? (r.monto / total) * 100 : 0,
    }));
  }

  get hayAntiguedad(): boolean {
    return this.antiguedad.some((r) => r.contenedores > 0);
  }

  get contenedoresEnPatio(): number {
    return this.antiguedad.reduce((s, r) => s + r.contenedores, 0);
  }

  /**
   * Lo detenido más de 30 días. Es el dato que justifica todo el bloque:
   * suelen ser poquísimos contenedores con una parte enorme de la plata.
   */
  get detenido(): any {
    const viejos = this.antiguedad.filter((r) => r.id === '31-60' || r.id === '60+');
    const contenedores = viejos.reduce((s, r) => s + r.contenedores, 0);
    const monto = viejos.reduce((s, r) => s + r.monto, 0);
    const pct = viejos.reduce((s, r) => s + r.pct, 0);
    return { contenedores, monto, pct };
  }

  /** Reparto del transporte, con su color y su participación. */
  get transporte(): any[] {
    const t = this.totals;
    if (!t || t.totalRetiros === undefined) { return []; }
    const filas = [
      { etiqueta: 'Piki 7', cantidad: t.porTipo.piki7 },
      { etiqueta: 'Externo', cantidad: t.porTipo.externo },
      { etiqueta: 'No requirió', cantidad: t.noRequirio },
      { etiqueta: 'Sin dato', cantidad: t.sinDato },
    ];
    const total = filas.reduce((s, f) => s + f.cantidad, 0);
    return filas.map((f) => ({
      ...f,
      color: this.COLORES_TRANSPORTE[f.etiqueta],
      pct: total > 0 ? (f.cantidad / total) * 100 : 0,
    }));
  }

  get hayTransporte(): boolean {
    return this.transporte.length > 0;
  }

  claseFila(fila: any): string {
    return (fila && this.CLASES_EDAD[fila.rangoAntiguedad]) || '';
  }

  claseSituacion(situacion: string): string {
    if (situacion === 'Facturado') { return 'mr-tag mr-tag--facturado'; }
    if (situacion === 'Estimado') { return 'mr-tag mr-tag--estimado'; }
    return 'mr-tag mr-tag--despues';
  }

  onTipoChange(): void {
    this.rows = [];
    this.totals = null;
    this.periodo = null;
    this.error = '';
    this.config.currentPage = 1;
  }

  onPageChange(pagina: number): void {
    this.config.currentPage = pagina;
  }

  generar(): void {
    const { startDate, endDate } = this.filtros.value;
    if (!startDate || !endDate) {
      this.error = 'Seleccione la fecha de inicio y la de fin';
      return;
    }

    this.loading = true;
    this.error = '';
    this.rows = [];
    this.totals = null;
    this.config.currentPage = 1;
    this.spinner.show();

    const params: any = { startDate, endDate };
    if (this.filtros.value.containerTypeId) {
      params.containerTypeId = this.filtros.value.containerTypeId;
    }

    let peticion;
    switch (this.tipoReporte) {
      case 'gate-out':
        peticion = this.transactionService.getGateOutReport(params);
        break;
      case 'storage-days':
        peticion = this.transactionService.getStorageDaysReport(params);
        break;
      case 'transport':
        peticion = this.transactionService.getTransportReport(params);
        break;
      default:
        peticion = this.transactionService.getGateInReport(params);
    }

    peticion.subscribe({
      next: (res: any) => {
        this.rows = res.rows || [];
        this.totals = res.totals || null;
        this.periodo = res.period || null;
        this.loading = false;
        this.spinner.hide();
        if (!this.rows.length) {
          this.error = 'No hay movimientos en el rango seleccionado';
        }
      },
      error: (err: any) => {
        this.error = (err && err.error && err.error.error) || 'Error al obtener el reporte';
        this.loading = false;
        this.spinner.hide();
      }
    });
  }

  exportarExcel(): void {
    if (!this.rows.length) { return; }

    // Se exporta con los títulos de columna como claves: son los
    // encabezados de la hoja, y salen del mismo mapa que la tabla.
    const filas = this.rows.map((fila) => {
      const salida: any = {};
      this.columnas.forEach((c) => { salida[c.titulo] = fila[c.campo]; });
      return salida;
    });

    const totales: any = {};
    this.columnas.forEach((c, i) => {
      totales[c.titulo] = i === 0 ? 'TOTALES' : '';
    });
    if (this.totals) {
      if (this.totals.montoTotal !== undefined) {
        totales['Monto'] = this.totals.montoTotal;
        totales['Descuento'] = this.totals.descuentoTotal;
      }
      if (this.totals.diasCobrablesTotal !== undefined) {
        totales['Cobrables'] = this.totals.diasCobrablesTotal;
        // Solo lo efectivamente facturado del periodo. Lo estimado y lo
        // que salió después van en el pie de la tabla, no en esta suma.
        totales['Monto'] = this.totals.montoFacturado;
        totales['Situación'] = 'Facturado del periodo';
      }
    }
    filas.push(totales);

    const tipo = this.TIPOS.find((t) => t.id === this.tipoReporte);
    const desde = this.periodo ? this.periodo.startDate : '';
    const hasta = this.periodo ? this.periodo.endDate : '';
    const anchos = this.columnas.map((c) => c.ancho || 15);
    this.excel.exportAsExcelFile(filas, `Reporte_${tipo.archivo}_${desde}_${hasta}`, anchos);
  }
}
